/**
 * ASS / SSA.
 *
 * There is no ISO spec. The de-facto references are the original SubStation Alpha v4.00
 * documentation, the ASS v4.00+ extension it grew into, **Aegisub's actual behaviour** (the
 * editor every subtitler uses), and **libass** where the two differ. Where they disagree,
 * libass decides rendering and Aegisub decides what a file looks like on disk, so this parser
 * follows Aegisub on structure and libass on semantics. Each of the following is a fixture:
 *
 *   • **The `Format:` line makes columns AUTHORITATIVE, not positional.** Both `[V4+ Styles]`
 *     and `[Events]` carry one, files in the wild reorder them, and a positional reader
 *     silently swaps `Effect` and `Text` on those files. The exact list is preserved per input
 *     in `AssPreservation` so the writer re-emits the SOURCE column order.
 *   • **The `Text` field takes everything after the last comma** — more precisely, everything
 *     after the (n−1)th, where n is the number of Format columns. Dialogue contains commas
 *     constantly; splitting on all of them truncates every line with one in it.
 *   • **`ScriptType: v4.00` vs `v4.00+`** selects the dialect. SSA v4 has no Layer, no
 *     ScaleX/ScaleY/Spacing/Angle/StrikeOut, carries `TertiaryColour` where ASS carries
 *     `OutlineColour`, and uses the 1/2/3 +4(top) +8(mid) alignment scheme rather than the
 *     numpad. Reading an SSA as ASS puts the subtitles in the wrong third of the screen.
 *   • **`WrapStyle` 0–3** and **`ScaledBorderAndShadow`** change rendering and are typed
 *     fields; `PlayResX/Y` is the basis every `\pos` is normalised against, and its absence is
 *     libass's 384×288 legacy fallback recorded as `provenance: 'assumed'`.
 *   • **`Comment:` events** are carried, never rendered, and stay orthogonal to `CueKind`, so
 *     a commented-out music line is still `kind: 'music'` for `removeSdh`.
 *   • **`\k` centiseconds** are durations that must sum to the cue duration. They frequently
 *     do not — that is `karaoke-coverage-short`, a diagnostic, not a repair.
 *
 * Sections this model does not type (`[Fonts]`, `[Graphics]`, `[Aegisub Project Garbage]`) are
 * preserved VERBATIM as lines, which round-trips them byte-exactly without needing to decode
 * the UUE attachment payload — decoding to re-encode identically would be work in exchange for
 * a new opportunity to corrupt an embedded font.
 */

import type {
  Alignment,
  AssPreservation,
  Cue,
  CueKind,
  Margins,
  ParseOptions,
  ParseResult,
  ParserDefinition,
  RawEvent,
  ReadFormat,
  Resolution,
  RunContext,
  Style,
  SubtitleDocument,
  WrapStyle,
} from '../../../contracts/cue-model.ts';
import { ASSUMED_RESOLUTION, CS_GRID, makeCue, originFor } from '../../core/model/build.ts';
import { parseTimestamp, timeFormat } from '../../core/time/timestamp.ts';
import { assAlpha, parseAssColor } from '../common/color.ts';
import { Draft, safeSpan } from '../common/draft.ts';
import { type SourceLine, scanLines } from '../common/lines.ts';
import { parseAssTags, ssaAlignmentToNumpad } from './ass-tags.ts';

const ASS_TIME = timeFormat('ass');

const V4PLUS_STYLE_FORMAT = [
  'Name',
  'Fontname',
  'Fontsize',
  'PrimaryColour',
  'SecondaryColour',
  'OutlineColour',
  'BackColour',
  'Bold',
  'Italic',
  'Underline',
  'StrikeOut',
  'ScaleX',
  'ScaleY',
  'Spacing',
  'Angle',
  'BorderStyle',
  'Outline',
  'Shadow',
  'Alignment',
  'MarginL',
  'MarginR',
  'MarginV',
  'Encoding',
];

const V4_STYLE_FORMAT = [
  'Name',
  'Fontname',
  'Fontsize',
  'PrimaryColour',
  'SecondaryColour',
  'TertiaryColour',
  'BackColour',
  'Bold',
  'Italic',
  'BorderStyle',
  'Outline',
  'Shadow',
  'Alignment',
  'MarginL',
  'MarginR',
  'MarginV',
  'AlphaLevel',
  'Encoding',
];

const ASS_EVENT_FORMAT = [
  'Layer',
  'Start',
  'End',
  'Style',
  'Name',
  'MarginL',
  'MarginR',
  'MarginV',
  'Effect',
  'Text',
];
const SSA_EVENT_FORMAT = [
  'Marked',
  'Start',
  'End',
  'Style',
  'Name',
  'MarginL',
  'MarginR',
  'MarginV',
  'Effect',
  'Text',
];

export { ASS_EVENT_FORMAT, SSA_EVENT_FORMAT, V4_STYLE_FORMAT, V4PLUS_STYLE_FORMAT };

const WRAP_STYLES: readonly WrapStyle[] = ['smart-top', 'end-of-line', 'none', 'smart-bottom'];

/** Splits a `Key: value` directive. Returns null for a comment or a bare line. */
function directive(text: string): { readonly key: string; readonly value: string } | null {
  const trimmed = text.trimStart();
  if (trimmed === '' || trimmed.startsWith(';') || trimmed.startsWith('!:')) return null;
  const colon = trimmed.indexOf(':');
  if (colon <= 0) return null;
  return { key: trimmed.slice(0, colon).trim(), value: trimmed.slice(colon + 1).trim() };
}

/** Splits into exactly `count` fields, the LAST of which keeps every remaining comma. */
export function splitFields(value: string, count: number): readonly string[] {
  const out: string[] = [];
  let rest = value;
  for (let i = 0; i < count - 1; i++) {
    const comma = rest.indexOf(',');
    if (comma === -1) {
      out.push(rest);
      rest = '';
      continue;
    }
    out.push(rest.slice(0, comma));
    rest = rest.slice(comma + 1);
  }
  out.push(rest);
  return out;
}

export function parseAssLike(
  format: 'ass' | 'ssa',
  text: string,
  opts: ParseOptions,
  ctx: RunContext,
): ParseResult {
  const lines = scanLines(text);
  const draft = new Draft({ alloc: ctx.alloc, opts, sourceFormat: format, grid: CS_GRID });

  const scriptInfo: [string, string][] = [];
  const sections: { name: string; lines: string[] }[] = [];
  let styleFormat: string[] = [];
  let eventFormat: string[] = [];
  let scriptType: string | null = null;
  let playResX: number | null = null;
  let playResY: number | null = null;
  let wrapStyle: WrapStyle = 'smart-top';
  let scaledBorderAndShadow = true;
  let collisions: 'normal' | 'reverse' = 'normal';
  let title: string | null = null;

  let section = '';
  let sawSection = false;
  const eventLines: { line: SourceLine; kind: 'Dialogue' | 'Comment'; value: string }[] = [];
  const styleLines: { line: SourceLine; value: string }[] = [];

  for (const line of lines) {
    const trimmed = line.text.trim();
    if (trimmed.startsWith('[') && trimmed.endsWith(']')) {
      section = trimmed.slice(1, -1);
      sawSection = true;
      const known =
        /^script info$/i.test(section) ||
        /^v4\+* styles$/i.test(section) ||
        /^v4 styles$/i.test(section) ||
        /^events$/i.test(section);
      if (!known) sections.push({ name: section, lines: [] });
      continue;
    }

    const lower = section.toLowerCase();
    if (lower === 'script info') {
      const d = directive(line.text);
      if (d === null) {
        if (trimmed !== '') scriptInfo.push([';', trimmed.replace(/^;\s?/, '')]);
        continue;
      }
      scriptInfo.push([d.key, d.value]);
      switch (d.key.toLowerCase()) {
        case 'title':
          title = d.value;
          break;
        case 'scripttype':
          scriptType = d.value;
          break;
        case 'playresx':
          playResX = intOr(d.value, null);
          break;
        case 'playresy':
          playResY = intOr(d.value, null);
          break;
        case 'wrapstyle': {
          const n = intOr(d.value, 0) ?? 0;
          wrapStyle = WRAP_STYLES[n] ?? 'smart-top';
          break;
        }
        case 'scaledborderandshadow':
          scaledBorderAndShadow = !/^no$/i.test(d.value);
          break;
        case 'collisions':
          collisions = /^reverse$/i.test(d.value) ? 'reverse' : 'normal';
          break;
        default:
          break;
      }
      continue;
    }

    if (/^v4\+* styles$/i.test(section) || /^v4 styles$/i.test(section)) {
      const d = directive(line.text);
      if (d === null) continue;
      if (/^format$/i.test(d.key)) {
        styleFormat = d.value.split(',').map((s) => s.trim());
        continue;
      }
      if (/^style$/i.test(d.key)) styleLines.push({ line, value: d.value });
      continue;
    }

    if (/^events$/i.test(section)) {
      const d = directive(line.text);
      if (d === null) continue;
      if (/^format$/i.test(d.key)) {
        eventFormat = d.value.split(',').map((s) => s.trim());
        continue;
      }
      if (/^dialogue$/i.test(d.key)) {
        eventLines.push({ line, kind: 'Dialogue', value: d.value });
        continue;
      }
      if (/^comment$/i.test(d.key)) {
        eventLines.push({ line, kind: 'Comment', value: d.value });
        continue;
      }
      continue;
    }

    const current = sections[sections.length - 1];
    if (current !== undefined && section === current.name) current.lines.push(line.text);
  }

  if (!sawSection) {
    return draft.notRecognized(
      'line 1: an ASS/SSA file begins with a bracketed section such as [Script Info]; none was found',
    );
  }

  const isSsa = format === 'ssa' || /v4\.00$/i.test(scriptType ?? '');
  if (styleFormat.length === 0) {
    styleFormat = isSsa ? [...V4_STYLE_FORMAT] : [...V4PLUS_STYLE_FORMAT];
    if (styleLines.length > 0) {
      draft.diag(
        'ass-format-column-unknown',
        `[${isSsa ? 'V4' : 'V4+'} Styles] has no Format: line; the ${isSsa ? 'v4.00' : 'v4.00+'} default column order was assumed`,
      );
    }
  }
  if (eventFormat.length === 0) {
    eventFormat = isSsa ? [...SSA_EVENT_FORMAT] : [...ASS_EVENT_FORMAT];
    if (eventLines.length > 0) {
      draft.diag(
        'ass-format-column-unknown',
        '[Events] has no Format: line; the default column order was assumed',
      );
    }
  }

  const resolution: Resolution =
    playResX !== null && playResY !== null
      ? { x: playResX, y: playResY, provenance: 'declared' }
      : ASSUMED_RESOLUTION;
  if (resolution.provenance === 'assumed' && styleLines.length > 0) {
    draft.diag(
      'resolution-assumed',
      'no PlayResX/PlayResY; libass’s legacy 384×288 fallback was assumed, so positions are advisory',
    );
  }

  /*── styles ───────────────────────────────────────────────────────────────────*/

  const styleColumnIndex = indexOf(styleFormat);
  let firstStyleId = draft.defaultStyleId;
  let firstStyleSeen = false;

  for (const entry of styleLines) {
    const fields = splitFields(entry.value, styleFormat.length);
    const get = (name: string): string => {
      const at = styleColumnIndex.get(name.toLowerCase());
      return at === undefined ? '' : (fields[at] ?? '').trim();
    };
    const known = new Set(styleFormat.map((c) => c.toLowerCase()));
    const rawColumns: Record<string, string> = {};
    styleFormat.forEach((column, at) => {
      const lc = column.toLowerCase();
      if (MODELLED_STYLE_COLUMNS.has(lc)) return;
      rawColumns[column] = (fields[at] ?? '').trim();
    });
    void known;

    const name = get('Name') || 'Default';
    const alignmentRaw = intOr(get('Alignment'), 2) ?? 2;
    const alignment: Alignment = isSsa
      ? (ssaAlignmentToNumpad(alignmentRaw) ?? 2)
      : ((alignmentRaw >= 1 && alignmentRaw <= 9 ? alignmentRaw : 2) as Alignment);

    const margins: Margins = {
      left: intOr(get('MarginL'), 0) ?? 0,
      right: intOr(get('MarginR'), 0) ?? 0,
      vertical: intOr(get('MarginV'), 0) ?? 0,
    };

    const style: Style = {
      id: ctx.alloc.style(),
      name,
      fontName: get('Fontname') || 'Arial',
      fontSize: numOr(get('Fontsize'), 48),
      primaryColor: parseAssColor(get('PrimaryColour')) ?? { r: 255, g: 255, b: 255, a: 1 },
      secondaryColor: parseAssColor(get('SecondaryColour')) ?? { r: 255, g: 0, b: 0, a: 1 },
      // SSA v4 calls it TertiaryColour and there is no separate outline colour.
      outlineColor: parseAssColor(isSsa ? get('TertiaryColour') : get('OutlineColour')) ?? {
        r: 0,
        g: 0,
        b: 0,
        a: 1,
      },
      shadowColor: parseAssColor(get('BackColour')) ?? { r: 0, g: 0, b: 0, a: 1 },
      // ASS writes -1 for true and 0 for false. Any non-zero is true, which is what libass does.
      bold: truthy(get('Bold')),
      italic: truthy(get('Italic')),
      underline: truthy(get('Underline')),
      strikeOut: truthy(get('StrikeOut')),
      scaleX: numOr(get('ScaleX'), 100),
      scaleY: numOr(get('ScaleY'), 100),
      spacing: numOr(get('Spacing'), 0),
      angle: numOr(get('Angle'), 0),
      // BorderStyle 1 = outline, 3 = opaque box. VSFilter's 4 is an opaque box with a shadow
      // band; libass maps it to 3, so this does too and says so.
      borderStyle: borderStyleOf(get('BorderStyle'), draft, name),
      outlineWidth: numOr(get('Outline'), 2),
      shadowDepth: numOr(get('Shadow'), 0),
      alignment,
      margins,
      encoding: intOr(get('Encoding'), 1) ?? 1,
      rawColumns,
      isSynthetic: false,
    };
    draft.addStyle(style);
    if (!firstStyleSeen) {
      firstStyleId = style.id;
      firstStyleSeen = true;
    }
  }
  if (firstStyleSeen) {
    // A style literally named "Default" is the document default when one exists; otherwise the
    // first declared style is. That is Aegisub's own resolution order.
    const named = draft.styleByName('Default');
    draft.useDeclaredStyles(named?.id ?? firstStyleId);
  }

  /*── events ───────────────────────────────────────────────────────────────────*/

  const eventColumnIndex = indexOf(eventFormat);
  const styleByName = (name: string): Style | undefined => draft.styleByName(name);
  let ordinal = 0;
  let firstFailure: string | null = null;

  for (const entry of eventLines) {
    const fields = splitFields(entry.value, eventFormat.length);
    const get = (name: string): string => {
      const at = eventColumnIndex.get(name.toLowerCase());
      return at === undefined ? '' : (fields[at] ?? '');
    };

    const start = parseTimestamp(get('Start').trim(), ASS_TIME);
    const end = parseTimestamp(get('End').trim(), ASS_TIME);
    if (start === null || end === null) {
      const message = `expected ASS timestamps like 0:00:12.34, found Start=${JSON.stringify(get('Start').trim())} End=${JSON.stringify(get('End').trim())}`;
      draft.diag('malformed-timestamp', message, { sourceLine: entry.line.no });
      firstFailure ??= `line ${entry.line.no}: ${message}`;
      continue;
    }

    const id = ctx.alloc.cue();
    const time = safeSpan(start as number, end as number, draft, {
      cueId: id,
      sourceLine: entry.line.no,
    });

    const styleName = get('Style').trim();
    const style = styleByName(styleName);
    if (style === undefined && styleName !== '') {
      draft.diag(
        'unknown-style-reference',
        `event references style "${styleName}", which [${isSsa ? 'V4' : 'V4+'} Styles] does not define; the default was used`,
        { cueId: id, sourceLine: entry.line.no },
      );
    }
    const resolvedStyle = style ?? currentDefaultStyle(draft);

    const tags = parseAssTags({
      text: get('Text'),
      alloc: ctx.alloc,
      cueStart: time.start,
      cueEnd: time.end,
      style: resolvedStyle,
      resolution,
      styleByName,
    });
    for (const name of tags.unknownNames) {
      draft.diag(
        'unknown-tag-args-unclassified',
        `override tag \\${name} is not modeled; its arguments were preserved but not classified, so a retime cannot rescale any time inside it`,
        { cueId: id, sourceLine: entry.line.no },
      );
    }

    // The \k durations should sum to the cue duration. They routinely do not, and that is a
    // finding about the file rather than something to repair: stretching them would move
    // measured syllable boundaries.
    if (tags.karaokeTicks > 0) {
      const duration = (time.end as number) - (time.start as number);
      const drift = Math.abs(duration - tags.karaokeTicks);
      if (drift > 1200) {
        draft.diag(
          'karaoke-coverage-short',
          `\\k durations total ${(tags.karaokeTicks / 1200).toFixed(2)}s against a ${(duration / 1200).toFixed(2)}s cue`,
          { cueId: id, sourceLine: entry.line.no },
        );
      }
    }

    const marginL = intOr(get('MarginL').trim(), 0) ?? 0;
    const marginR = intOr(get('MarginR').trim(), 0) ?? 0;
    const marginV = intOr(get('MarginV').trim(), 0) ?? 0;
    const margins: Margins | null =
      marginL === 0 && marginR === 0 && marginV === 0
        ? null
        : { left: marginL, right: marginR, vertical: marginV };

    const actor = get('Name').trim() || null;
    const effect = get('Effect').trim() || null;
    const layer = isSsa ? 0 : (intOr(get('Layer').trim(), 0) ?? 0);

    const extra: Record<string, string> = {};
    eventFormat.forEach((column, at) => {
      if (MODELLED_EVENT_COLUMNS.has(column.toLowerCase())) return;
      extra[column] = (fields[at] ?? '').trim();
    });

    const raw: RawEvent | null = opts.retainRaw
      ? {
          format,
          fields: Object.fromEntries(eventFormat.map((c, at) => [c, fields[at] ?? ''])),
          timeFields: ['Start', 'End'],
          stale: false,
        }
      : null;

    const base = makeCue({
      id,
      time,
      lines: tags.lines,
      styleId: resolvedStyle.id,
      trackId: draft.defaultTrackId,
      origin: { ...originFor(draft.inputId, 'parse', ordinal), sourceLine: entry.line.no },
      layer,
      granularity: tags.granularity,
    });

    const cue: Cue = {
      ...base,
      kind: classify(base, effect),
      commented: entry.kind === 'Comment',
      margins,
      position: tags.position,
      fade: tags.fade,
      wrap: tags.wrap,
      karaoke: tags.karaokeKind,
      actor,
      effect,
      extra,
      raw,
      flags: {
        ...base.flags,
        hasFragileTags: tags.hasFragileTags,
        hasRelativeTimes: tags.hasRelativeTimes,
      },
    };
    draft.addCue(cue);
    ordinal++;
  }

  if (draft.cues.length === 0 && firstFailure !== null) {
    return draft.notRecognized(firstFailure);
  }

  const preservation: AssPreservation = {
    scriptInfo: scriptInfo.map(([k, v]) => [k, v] as const),
    eventFormat,
    styleFormat,
    scriptType,
    sections: sections.map((s) => ({ name: s.name, lines: s.lines })),
    // Attachments survive VERBATIM inside `sections` as their own UUE lines, which round-trips
    // an embedded font byte-exactly. Decoding to re-encode would be work in exchange for a new
    // way to corrupt one.
    attachments: [],
  };

  draft.setTitle(title);
  const doc = draft.document();
  const withAss: SubtitleDocument = {
    ...doc,
    ass: { [draft.inputId]: preservation },
    meta: {
      ...doc.meta,
      resolution,
      wrapStyle,
      scaledBorderAndShadow,
      collisions,
    },
    inputs: doc.inputs.map((i) => (i.id === draft.inputId ? { ...i, resolution } : i)),
  };
  return { ok: true, doc: withAss, diagnostics: draft.diagnostics };
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Helpers
───────────────────────────────────────────────────────────────────────────────────────*/

const MODELLED_STYLE_COLUMNS = new Set([
  'name',
  'fontname',
  'fontsize',
  'primarycolour',
  'secondarycolour',
  'outlinecolour',
  'tertiarycolour',
  'backcolour',
  'bold',
  'italic',
  'underline',
  'strikeout',
  'scalex',
  'scaley',
  'spacing',
  'angle',
  'borderstyle',
  'outline',
  'shadow',
  'alignment',
  'marginl',
  'marginr',
  'marginv',
  'encoding',
]);

const MODELLED_EVENT_COLUMNS = new Set([
  'layer',
  'marked',
  'start',
  'end',
  'style',
  'name',
  'marginl',
  'marginr',
  'marginv',
  'effect',
  'text',
]);

function indexOf(columns: readonly string[]): ReadonlyMap<string, number> {
  const out = new Map<string, number>();
  columns.forEach((c, i) => {
    const key = c.trim().toLowerCase();
    if (!out.has(key)) out.set(key, i);
  });
  // SSA calls the layer column `Marked`; a v4 file read as ASS still needs `Layer` to resolve.
  if (!out.has('layer') && out.has('marked')) out.set('layer', out.get('marked') as number);
  return out;
}

function intOr(value: string, fallback: number | null): number | null {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n : fallback;
}

function numOr(value: string, fallback: number): number {
  const n = Number.parseFloat(value);
  return Number.isFinite(n) ? n : fallback;
}

function truthy(value: string): boolean {
  const n = Number.parseInt(value, 10);
  return Number.isFinite(n) ? n !== 0 : /^(yes|true)$/i.test(value.trim());
}

function borderStyleOf(value: string, draft: Draft, styleName: string): 'outline' | 'opaque-box' {
  const n = Number.parseInt(value, 10);
  if (n === 3) return 'opaque-box';
  if (n === 4) {
    draft.diag(
      'malformed-tag',
      `style "${styleName}" uses VSFilter BorderStyle 4; libass renders it as an opaque box (3) and so do we`,
    );
    return 'opaque-box';
  }
  return 'outline';
}

function currentDefaultStyle(draft: Draft): Style {
  const table = draft.styleTable();
  const style = table.byId[table.defaultId];
  if (style !== undefined) return style;
  throw new RangeError('ass: style table has no default, which Draft guarantees');
}

/**
 * `CueKind`, classified ONCE at parse so twenty transforms do not re-run speaker and SDH
 * regexes over the same text.
 *
 * A POSITIONED cue with no dialogue punctuation is a sign; `♪`-wrapped text is music; a
 * fully-bracketed cue is a sound effect. Everything else is dialogue, which is the safe
 * default because the classification only ever makes a cue MORE eligible for removal.
 */
function classify(cue: Cue, effect: string | null): CueKind {
  let text = '';
  for (const line of cue.lines)
    for (const run of line.runs) if (run.kind !== 'drawing') text += run.text;
  const t = text.trim();
  if (t === '') return cue.position?.point != null || effect !== null ? 'sign' : 'dialogue';
  if (/^[♪♫]/.test(t) || /[♪♫]$/.test(t)) return 'music';
  if (/^[[(（【].*[\])）】]$/s.test(t)) return 'sound';
  if (cue.position?.point != null) return 'sign';
  return 'dialogue';
}

/*───────────────────────────────────────────────────────────────────────────────────────
  Detection
───────────────────────────────────────────────────────────────────────────────────────*/

function sniffAssLike(text: string, filename: string | null, want: 'ass' | 'ssa'): number {
  const head = text.slice(0, 4096);
  if (!/\[Script Info\]/i.test(head)) return 0;
  const declared = /ScriptType:\s*(v4\.00\+*)/i.exec(head)?.[1]?.toLowerCase() ?? null;
  const hasV4Plus = /\[V4\+ Styles\]/i.test(text) || declared === 'v4.00+';
  const hasV4 = /\[V4 Styles\]/i.test(text) || declared === 'v4.00';
  const ext = (filename ?? '').toLowerCase();

  if (want === 'ass') {
    if (hasV4Plus) return ext.endsWith('.ass') ? 1 : 0.95;
    if (hasV4) return 0.1; // it parses, but the ssa reader is the right one
    return ext.endsWith('.ass') ? 0.7 : 0.5;
  }
  if (hasV4 && !hasV4Plus) return ext.endsWith('.ssa') ? 1 : 0.95;
  if (hasV4Plus) return 0.1;
  return ext.endsWith('.ssa') ? 0.7 : 0.3;
}

export const assParser: ParserDefinition = {
  format: 'ass',
  sniff: (text, filename) => sniffAssLike(text, filename, 'ass'),
  requiresFramerate: false,
  parse: (text, opts, ctx) => parseAssLike('ass', text, opts, ctx),
};

export const ssaParser: ParserDefinition = {
  format: 'ssa',
  sniff: (text, filename) => sniffAssLike(text, filename, 'ssa'),
  requiresFramerate: false,
  parse: (text, opts, ctx) => parseAssLike('ssa', text, opts, ctx),
};

export type { ReadFormat };
export { assAlpha };
